import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

const categories = [
  { name: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
  { name: 'Hoodies/Sweaters', url: 'https://kaspreadsheet.com/category-912-Hoodies%2FSweaters.html' },
  { name: 'T-Shirts', url: 'https://kaspreadsheet.com/category-913-T-Shirts.html' },
  { name: 'Jackets', url: 'https://kaspreadsheet.com/category-914-Jackets.html' },
  { name: 'Pants/Shorts', url: 'https://kaspreadsheet.com/category-915-Pants%2FShorts.html' },
  { name: 'Headwear', url: 'https://kaspreadsheet.com/category-916-Headwear.html' },
  { name: 'Sets', url: 'https://kaspreadsheet.com/category-917-Sets.html' },
  { name: 'Underwear/Underpants', url: 'https://kaspreadsheet.com/category-918-underwear%2Funderpants.html' },
  { name: 'Jersey', url: 'https://kaspreadsheet.com/category-919-Jersey.html' },
  { name: 'Accessories', url: 'https://kaspreadsheet.com/category-920-Accessories.html' },
  { name: 'Bags', url: 'https://kaspreadsheet.com/category-921-Bags.html' },
  { name: 'Belts', url: 'https://kaspreadsheet.com/search-922/name/Bags.html' },
  { name: 'Perfumes', url: 'https://kaspreadsheet.com/search-923/name/Bags.html' },
  { name: 'OtherStuff', url: 'https://kaspreadsheet.com/search-924/name/Bags.html' },
];

const categoryIcons: Record<string, string> = {
  'Shoes': 'ri-footprint-line',
  'Hoodies/Sweaters': 'ri-t-shirt-line',
  'T-Shirts': 'ri-t-shirt-line',
  'Jackets': 'ri-t-shirt-line',
  'Pants/Shorts': 'ri-pantone-line',
  'Headwear': 'ri-base-station-line',
  'Sets': 'ri-stack-line',
  'Underwear/Underpants': 'ri-checkbox-blank-line',
  'Jersey': 'ri-football-line',
  'Accessories': 'ri-vip-diamond-line',
  'Bags': 'ri-briefcase-line',
  'Belts': 'ri-price-tag-3-line',
  'Perfumes': 'ri-flask-line',
  'OtherStuff': 'ri-more-line',
};

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleScroll() {
      setIsScrolled(window.scrollY > 10);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/98 backdrop-blur-lg border-b border-background-200'
          : 'bg-background-50/95 backdrop-blur-md border-b border-transparent'
      }`}>
        <div className="w-full px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-2 group cursor-pointer shrink-0" onClick={closeMobileMenu}>
              <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-file-list-3-line text-white text-base md:text-lg"></i>
              </div>
              <span className="text-base md:text-lg font-bold text-foreground-950 font-heading tracking-tight whitespace-nowrap">
                Sugargoo<span className="text-primary-500">Spreadsheet</span><span className="text-foreground-400">.run</span>
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-4 lg:gap-6">
              <Link to="/" className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer px-1">Home</Link>
              <Link to="/articles/what-is-sugargoo-spreadsheet" className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer px-1">Shopping Guide</Link>

              <div ref={dropdownRef} className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-1.5 px-5 py-2.5 bg-foreground-900 text-background-50 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Categories
                  {isDropdownOpen ? (
                    <i className="ri-arrow-up-s-line text-sm"></i>
                  ) : (
                    <i className="ri-arrow-down-s-line text-sm"></i>
                  )}
                </button>

                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-foreground-900 rounded-xl overflow-hidden z-50" style={{ animation: 'fadeIn 150ms ease-out' }}>
                    <div className="py-2 max-h-[420px] overflow-y-auto">
                      {categories.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-5 py-2.5 text-sm text-background-50/85 hover:text-background-50 hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          <i className={`${categoryIcons[cat.name] || 'ri-price-tag-3-line'} text-base w-5 text-center`}></i>
                          {cat.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-900 cursor-pointer shrink-0"
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-background-50 overflow-y-auto"
          style={{ animation: 'slideDown 200ms ease-out' }}
        >
          <div className="pt-20 pb-8 px-4 space-y-6">
            <div className="space-y-1">
              <Link
                to="/"
                className="flex items-center gap-3 py-3.5 px-4 rounded-xl text-base font-semibold text-foreground-900 bg-background-100 cursor-pointer"
                onClick={closeMobileMenu}
              >
                <span className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className="ri-home-4-line text-primary-600 text-lg"></i>
                </span>
                Home
              </Link>
              <Link
                to="/articles/what-is-sugargoo-spreadsheet"
                className="flex items-center gap-3 py-3.5 px-4 rounded-xl text-base font-semibold text-foreground-900 bg-background-100 cursor-pointer"
                onClick={closeMobileMenu}
              >
                <span className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center">
                  <i className="ri-book-open-line text-accent-600 text-lg"></i>
                </span>
                Shopping Guide
              </Link>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <div className="flex-1 h-px bg-background-200"></div>
              <span className="text-xs font-bold text-foreground-400 uppercase tracking-widest font-label whitespace-nowrap">Browse Categories</span>
              <div className="flex-1 h-px bg-background-200"></div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2.5 py-3 px-3 rounded-xl text-sm text-foreground-700 bg-background-100 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                  onClick={closeMobileMenu}
                >
                  <span className="w-8 h-8 rounded-lg bg-background-200/70 flex items-center justify-center shrink-0">
                    <i className={`${categoryIcons[cat.name] || 'ri-price-tag-3-line'} text-foreground-500 text-sm`}></i>
                  </span>
                  <span className="truncate text-xs font-medium">{cat.name}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}